<script setup lang="ts">
import head from 'lodash/head';
import type { OptionsModel } from '#layers/cms/app/pages/cms/management/websites/_runtime/services/WebsitesService';
import WebsitesService from '#layers/cms/app/pages/cms/management/websites/_runtime/services/WebsitesService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';

const props = withDefaults(defineProps<{
    websiteId: number;
    locale: string;
    disabled?: boolean;
}>(), {
    disabled: false
});

const emit = defineEmits<{
    (e: 'change-website', value: number): void;
    (e: 'change-locale', value: string): void;
}>();

const { $log } = useLogging();
const store = useWebsitesStore();
const modelWebsiteData = ref<number>(null);
const modelLocaleData = ref('');

const websites = computed({
    get: () => store.options,
    set: (options: OptionsModel[]) => { store.options = options; }
});

const website = computed(() =>
{
    return head(websites.value.filter(p => p.id == modelWebsite.value || 0));
});

const modelWebsite = computed({
    get: () => modelWebsiteData.value,
    set: (value: number) => { modelWebsiteData.value = value; }
});

const modelLocale = computed({
    get: () => modelLocaleData.value,
    set: (value: string) => { modelLocaleData.value = value; }
});

const locales = computed(() =>
{
    const websiteTmp = website.value || { locales: [] } as OptionsModel;

    return websiteTmp.locales.map((p) =>
    {
        return { value: p, text: p.toUpperCase() };
    });
});

onBeforeMount(async (): Promise<void> =>
{
    await loadWebsites();
});

async function loadWebsites(): Promise<void>
{
    try
    {
        if (!websites.value || websites.value.length == 0)
        {
            websites.value = await WebsitesService.getOptions();
        }

        modelWebsite.value = props.websiteId ?? websites.value[0].id;
        modelLocale.value = props.locale.length ? props.locale : head(websites.value[0].locales) || null;

        if (!props.websiteId || !props.locale)
        {
            onWebsiteChanged(String(modelWebsite.value));
            onLocaleChanged(modelLocale.value);
        }
    }
    catch (ex)
    {
        websites.value = [];
        modelWebsite.value = null;
        modelLocale.value = null;
    }
}

function handleCurrentWebsiteChange(id: string): number
{
    try
    {
        store.current.website = Number(id);
        modelWebsite.value = Number(id);

        return Number(id);
    }
    catch (ex)
    {
        $log.debug(ex);
    }
}

function onWebsiteChanged(value: string): number
{
    const website = handleCurrentWebsiteChange(value);

    emit('change-website', website);

    return website;
}

function onLocaleChanged(value: string): string
{
    emit('change-locale', value);

    return value;
}

watch(() => props.websiteId, (value: string) =>
{
    modelWebsite.value = Number(value);
    handleCurrentWebsiteChange(value);
});

watch(() => props.locale, (value: string) =>
{
    modelLocale.value = value;
});
</script>

<template>
    <div class="row g-0">
        <div class="col-8 pe-1">
            <IdeoFormSelect
                v-model="modelWebsite"
                name="website"
                :options="websites"
                value-field="id"
                text-field="title"
                :disabled="websites.length === 0 || disabled"
                @update:model-value="onWebsiteChanged"
            />
        </div>
        <div class="col-4">
            <IdeoFormSelect
                v-model="modelLocale"
                name="locales"
                :options="locales"
                value-field="value"
                text-field="text"
                :disabled="locales.length === 0 || disabled"
                @update:model-value="onLocaleChanged"
            />
        </div>
    </div>
</template>
